import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getRequiredDocAmt,
  useRequiredDocsModal,
  UseRequiredDocsModalProps
} from 'src/pages/DUPPage/useRequiredDocsModal';
import { UnauthenticatedSessionProofType } from 'src/types/api';
import { StepType } from 'src/utils/telemetry';

export type StepsProps = ReturnType<typeof useWizardSteps>;

export enum StepId {
  PersonalInfo = 'personalInfo',
  ProofOfIncome = 'proofOfIncome',
  SupportingDocuments = 'supportingDocuments',
  ReviewAndSubmit = 'reviewAndSubmit',
  Success = 'success'
}

export interface StepInfoAttributes {
  'data-step-number': string;
  'data-step-title': string;
  'data-step-id': string;
}

export interface WizardStepInfo {
  stepId: StepId;
  title: string;
  description: string;
  stepInfoAttributes: StepInfoAttributes;
}

export interface StepConfig {
  stepId: StepId;
  stepType: StepType;
}

export const STEPS_CONFIG: StepConfig[] = [
  { stepId: StepId.PersonalInfo, stepType: 'personal_info' },
  { stepId: StepId.ProofOfIncome, stepType: 'proof_of_income' },
  { stepId: StepId.SupportingDocuments, stepType: 'supporting_documents' },
  { stepId: StepId.ReviewAndSubmit, stepType: 'review_and_submit' },
  { stepId: StepId.Success, stepType: 'success' }
];

export const getStepByIndex = (index: number) => STEPS_CONFIG[index];

export function useWizardSteps({
  connectedPayrollEnabled,
  property,
  ...rest
}: {
  connectedPayrollEnabled: boolean;
} & UseRequiredDocsModalProps) {
  const { t } = useTranslation(['steps', 'shared', 'default']);

  const [activeStepIdx, setActiveStepIdx] = useState(0);
  const activeStepId = getStepByIndex(activeStepIdx)?.stepId;

  const { onNext, RequiredDocsModal } = useRequiredDocsModal({ property, ...rest });

  const handleNext = ({
    hasPayrollAccountsConnected,
    hasBankAccountsConnected
  }: {
    hasPayrollAccountsConnected: boolean;
    hasBankAccountsConnected: boolean;
  }) => {
    const shouldContinue = onNext({
      stepId: activeStepId,
      hasPayrollAccountsConnected,
      hasBankAccountsConnected
    });
    if (shouldContinue) setActiveStepIdx((prevStep) => prevStep + 1);
  };

  const info: WizardStepInfo[] = STEPS_CONFIG.map((stepConfig, index) => {
    const { stepId, stepType } = stepConfig;
    const docAmount = getRequiredDocAmt(UnauthenticatedSessionProofType.BankStatement, property);

    let description = t(`${stepId}.description`, { count: docAmount });
    if (stepId === 'proofOfIncome' && connectedPayrollEnabled) {
      description = t(`${stepId}.descriptionConnectedPayroll`);
    }

    const stepNumber = index + 1;

    return {
      stepNumber,
      stepId,
      title: t(`${stepId}.title`),
      description,
      stepInfoAttributes: {
        'data-step-number': `${stepNumber}`,
        'data-step-title': t(`${stepId}.title`, { lng: 'en' }),
        'data-step-id': stepType
      }
    };
  });

  return {
    info,
    activeStepIdx,
    activeStepId,
    setActiveStepIdx,
    onFinished: handleNext,
    goBack: () => setActiveStepIdx((prevStep) => prevStep - 1),
    RequiredDocsModal
  };
}
